import { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
  Skeleton,
  Alert,  
  useMediaQuery, useTheme 
} from "@mui/material";
import {
  FavoriteBorder,
  ShoppingCartOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { getData } from "../../Webservice/Webservice";
import { useNavigate } from "react-router-dom";

const rubikfont = {
  fontFamily: "Rubik, san-serif"
};

export default function Tabsection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("");
  const [tabs, setTabs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
   const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchTabs = async () => {
      try {
        setLoading(true);
        const response = await getData('homepage/tabs/list?productPage=1&productLimit=10');
        
        if (response && response.tabs && response.tabs.length > 0) {
          setTabs(response.tabs);
          // Set first tab as active by default
          if (response.tabs[0].name) {
            setActiveTab(response.tabs[0].name);
          }
          // Set products from the first tab by default
          if (response.tabs[0].product_id && response.tabs[0].product_id.length > 0) {
            setProducts(response.tabs[0].product_id);
          }
        }
      } catch (err) {
        console.error('Error fetching tabs:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTabs();
  }, []);

  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);
    try {
      const response = await getData(`homepage/tabs/list?productPage=1&productLimit=10`);
      const selectedTab = response.tabs.find(tab => tab.name === newValue);
      if (selectedTab && selectedTab.product_id) {
        setProducts(selectedTab.product_id);
      }
    } catch (err) {
      console.error('Error fetching tab products:', err);
      setError('Failed to load products for this tab.');
    }
  };

  const formatPrice = (price) => {
    return `₹${parseFloat(price || 0).toFixed(2)}`;
  };

    const handleProductClick = (productId) => {
        navigate(`/product/${productId}`);
        window.scrollTo(0, 0);
    };

  return (
    <Paper
      sx={{
        py: { xs: 5, md: 8 },
        px: { xs: 2, md: 4 },
        boxShadow: "none",
      }}
    >
      {loading ? (
        <>
          {/* Skeleton Tabs */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, md: 2 },
                justifyContent: { xs: "flex-start", md: "center" },
                overflowX: { xs: "auto", md: "visible" },
                px: { xs: 1, md: 0 },
              }}
            >
              {[...Array(4)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  sx={{
                    width: { xs: 80, sm: 100, md: 120 },
                    height: 40,
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Skeleton Product Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(5, 1fr)",
              },
              gap: { xs: 2, sm: 3, md: 4 },
              minHeight: "300px",
            }}
          >
            {[...Array(10)].map((_, index) => (
              <Box key={index}>
                {/* Skeleton Image */}
                <Skeleton
                  variant="rectangular"
                  sx={{
                    width: "100%",
                    paddingTop: "90%",
                    borderRadius: 2,
                    marginBottom: 1,
                  }}
                />
                {/* Skeleton Title */}
                <Skeleton
                  variant="text"
                  sx={{
                    width: "90%",
                    height: { xs: 20, sm: 22, md: 24 },
                    marginBottom: 1,
                  }}
                />
                {/* Skeleton Price */}
                <Skeleton
                  variant="text"
                  sx={{
                    width: "60%",
                    height: { xs: 18, sm: 20, md: 22 },
                  }}
                />
              </Box>
            ))}
          </Box>
        </>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "standard"}
            centered={!isMobile}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
            sx={{
              mb: 4,
              minHeight: 48,
              px: { xs: 1, md: 0 },
              "& .MuiTabs-indicator": {
                display: "none",
              },
              overflowX: { xs: "auto", md: "visible" },
              "& .MuiTab-root": {
                textTransform: "uppercase",
                fontSize: { xs: "11px", sm: "12px", md: "14px" },
                fontWeight: 500,
                minHeight: 40,
                px: { xs: 1.5, sm: 2.5, md: 3 },
                mx: { xs: 0.5, md: 1 },
                borderRadius: 1,
                color: "#555",
                backgroundColor: "transparent",
                transition: "all 0.3s ease",
                whiteSpace: "nowrap",
                ...rubikfont,
              },
              "& .Mui-selected": {
                color: "white !important",
                backgroundColor: "#000",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#000",
                },
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab._id}
                label={tab.name}
                value={tab.name}
                sx={{
                  textTransform: "capitalize",
                }}
              />
            ))}
          </Tabs>

          {/* Product Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(5, 1fr)",
              },
              gap: { xs: 2, sm: 3, md: 4 },
              minHeight: "300px",
            }}
          >
            {products.length > 0 ? (
              products.map((product, index) => (
                <Box
                  key={index}
                  className="product-card"
                  sx={{
                    bgcolor: "white",
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    border: "1px solid #f0f0f0",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                      "& .product-image": { transform: "scale(1.08)" },
                      "& .action-icons": {
                        transform: "translateY(-50%) translateX(0)",
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {/* Image + Hover Icons */}
                  <Box
                    sx={{
                      position: "relative",
                      paddingTop: "90%",
                      overflow: "hidden",
                      bgcolor: "#f8f8f8",
                    }}
                  >
                    <img
                      src={
                        product.product_images &&
                        product.product_images[0]
                          ? product.product_images[0]
                          : "https://via.placeholder.com/300"
                      }
                      alt={product.title || "Product"}
                      className="product-image"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.4s ease",
                      }}
                    />
                      {/* ✅ B2B Discount Badge – TOP LEFT */}
                                    {product.b2b_discountValue && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 10,
                                          left: 10,
                                          bgcolor: "#FF6B35",
                                          color: "white",
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: 1,
                                          fontSize: { xs: "11px", sm: "12px", md: "13px" },
                                          fontWeight: 600,
                                          zIndex: 3,
                                          boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                                          ...rubikfont,
                                        }}
                                      >
                                        {product.b2b_discountType === "percentage"
                                          ? `${product.b2b_discountValue}% OFF`
                                          : `₹${product.b2b_discountValue} OFF`}
                                      </Box>
                                    )}

                    <Box
                      className="action-icons"
                      sx={{
                        position: "absolute",
                        right: 10,
                        top: "40%",
                        transform: "translateY(-50%) translateX(60px)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        opacity: 0,
                        transition: "all 0.4s ease",
                      }}
                    >
                      {[ VisibilityOutlined].map(
                        (Icon, i) => (
                          <IconButton
                            onClick={() => handleProductClick(product._id)}
                            key={i}
                            sx={{
                              bgcolor: "white",
                              width: { xs: 35, sm: 40, md: 45 },
                              height: { xs: 35, sm: 40, md: 45 },
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              "&:hover": {
                                bgcolor: "#222",
                                color: "white",
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <Icon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                          </IconButton>
                        )
                      )}
                    </Box>
                  </Box>

                  {/* Details */}
                  <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", sm: "16px", md: "18px" },
                        fontWeight: 500,
                        color: "#222",
                        ...rubikfont,
                        minHeight: { xs: "36px", sm: "44px", md: "48px" },
                      }}
                    >
                      {product.title}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: "16px", sm: "18px", md: "20px" },
                          fontWeight: 600,
                          color: "#222",
                          ...rubikfont,
                        }}
                      >
                        {formatPrice(product.b2b_sellingPrice)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "13px", sm: "14px", md: "16px" },
                          color: "red",
                          textDecoration: "line-through",
                          ...rubikfont,
                        }}
                      >
                        {formatPrice(product.b2c_mrp)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  py: 4,
                  ...rubikfont,
                }}
              >
                <Typography>No products found in this category.</Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
}